/**
 * The NATIVE-GRAIN view of rate-bearing recommendations (CLE-2470).
 *
 * A facility contract is not written in CPT codes. It is written in revenue codes, DRGs and
 * service-line categories — "Physical Therapy, Rev 0420-0429, $128 per visit" — and Ani reads it that
 * way: one row per priced line, the contract's own vocabulary, methodology and flags visible together.
 * Only afterwards does any of it become CPT-keyed config.
 *
 * The wizard renders one editable card per construct, which is the right surface for *fixing a field*
 * and the wrong one for *reading a rate sheet*: a 16-line outpatient grid becomes 16 stacked cards, and
 * whether two lines disagree about methodology is invisible. This module derives the table view instead
 * — one row per recommendation, grouped by service line.
 *
 * It is deliberately PURE and display-only. Nothing here writes; the row carries its recommendation id
 * so the grid can hand editing back to `ProposedModelingForm`, which remains the single place a value
 * is changed and validated. Derivation is lossy on purpose — it answers "what does this line say?", not
 * "what are all its fields?".
 */

import {
  DraftConfidence,
  IDraftRecommendation,
} from "../../../store/queries/contractModelDrafts";
import { careSettingOf, serviceLineOf } from "./steps";

/** Rows with no service line group under this rather than vanishing or forming a nameless group. */
export const UNGROUPED = "Unassigned";

export type CodeKind = "cpt" | "drg" | "revenue" | "none";

export interface IRateGridRow {
  id: string;
  /** What the line is, in the contract's words where we have them. */
  description: string;
  codes: string[];
  codeKind: CodeKind;
  /** How it pays — "Per Diem", "% of Billed Charges", "Case Rate". */
  methodology: string;
  /** Formatted for display; empty when the construct carries no single rate. */
  rate: string;
  bundled: boolean;
  carveOut: boolean;
  serviceLine: string;
  componentType: string;
  confidence: DraftConfidence | null;
  status: string;
  /** True when the agent's own numbers are unusable — the row is shown but flagged. */
  needsInput: boolean;
}

const modeling = (rec: IDraftRecommendation): Record<string, unknown> =>
  (rec.proposedModeling ?? {}) as Record<string, unknown>;

const str = (v: unknown): string | null =>
  typeof v === "string" && v.trim().length > 0 ? v.trim() : null;

const num = (v: unknown): number | null => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v))) {
    return Number(v);
  }
  return null;
};

const codeList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((c): c is string => typeof c === "string") : [];

/**
 * The codes the contract actually named this line by, preferring the most native grain available.
 *
 * Revenue codes and DRGs beat CPT deliberately: when a recommendation carries both, the rev code is
 * what the contract said and the CPT list is our expansion of it. Showing the expansion here would be
 * showing our own working back to the reviewer instead of the contract.
 */
export const codesOf = (
  rec: IDraftRecommendation
): { codes: string[]; kind: CodeKind } => {
  const m = modeling(rec);

  const revenue = Array.isArray(m.lines)
    ? (m.lines as Record<string, unknown>[]).flatMap((l) =>
        codeList(l?.revenueCodes)
      )
    : codeList(m.revenueCodes);
  if (revenue.length > 0) return { codes: revenue, kind: "revenue" };

  const drg = codeList(m.drgCodes);
  if (drg.length > 0) return { codes: drg, kind: "drg" };

  const cpt = [
    ...codeList(m.cptCodes),
    ...codeList(m.triggerCptCodes),
    ...codeList(m.bundledCptCodes),
  ];
  if (cpt.length > 0) return { codes: cpt, kind: "cpt" };

  const carveoutRows = Array.isArray(m.rows)
    ? (m.rows as Record<string, unknown>[])
        .map((r) => str(r?.code))
        .filter((c): c is string => !!c)
    : [];
  if (carveoutRows.length > 0) return { codes: carveoutRows, kind: "cpt" };

  return { codes: [], kind: "none" };
};

const INPATIENT_CONFIG_LABELS: Record<string, string> = {
  FIXED_COST: "Fixed cost outlier",
  PASS_THROUGH: "Pass-through",
  STOP_LOSS: "Stop loss",
  PER_DIEM: "Per Diem",
  DRG: "% of DRG",
  CASE_RATE: "Case Rate",
  INPATIENT_OUTLIER: "Outlier",
};

/**
 * How the line pays. `componentType` already IS the method tag, so this is mostly a rename into the
 * vocabulary a contract uses — with the two cases where one construct covers two methods
 * (`rate_calculation`'s percent-vs-unit, `case_rate_ruleset`'s per-diem flag) resolved from the data.
 */
export const methodologyOf = (rec: IDraftRecommendation): string => {
  const m = modeling(rec);
  const rateType = str(m.rateType);

  switch (rec.componentType) {
    case "rate_calculation":
      return rateType === "unit" ? "$ per unit" : "% of base rate file";
    case "carveout":
      return rateType === "unit" ? "Carve-out ($/unit)" : "Carve-out";
    case "case_rate_ruleset":
      return m.isPerDiem === true ? "Per Diem" : "Case Rate";
    case "package_ruleset":
      return "Package";
    case "charge_based_pricing":
      return "% of Billed Charges";
    case "lesser_of_charges":
      return "Lesser of";
    case "mpdr_ruleset":
      return "MPDR";
    case "exploding_fee_schedule":
      return "Exploding fee";
    case "unit_cap_ruleset":
      return "Unit cap";
    case "inpatient_ruleset": {
      const config =
        m.config && typeof m.config === "object"
          ? (m.config as Record<string, unknown>)
          : {};
      const type = str(config.type);
      return type ? INPATIENT_CONFIG_LABELS[type] ?? type : "Inpatient";
    }
    case "base_rate_file":
      return "Base rate file";
    default:
      return rec.componentType.replace(/_/g, " ");
  }
};

const money = (cents: number): string =>
  `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * The line's headline number, formatted.
 *
 * Units are NOT consistent across constructs — `ratePercent` is whole percent unless `rateType` is
 * `unit`, when it is cents; ruleset `multiplier` is a decimal fraction. Rendering the raw number would
 * put "0.85" and "85" in the same column meaning the same thing, so each is formatted from its own
 * declared unit. Constructs with no single rate (a package, a unit cap) return "" rather than a zero.
 */
export const rateOf = (rec: IDraftRecommendation): string => {
  const m = modeling(rec);
  const rateType = str(m.rateType);

  if (Array.isArray(m.lines) && m.lines.length > 0) {
    const rates = (m.lines as Record<string, unknown>[])
      .map((l) => num(l?.rateInCents))
      .filter((c): c is number => c != null);
    if (rates.length === 1) return money(rates[0]);
    if (rates.length > 1) return `${rates.length} rates`;
  }

  const ratePercent = num(m.ratePercent);
  if (ratePercent != null) {
    return rateType === "unit" ? money(ratePercent) : `${ratePercent}%`;
  }

  const multiplier = num(m.multiplier);
  if (multiplier != null) return `${Math.round(multiplier * 100)}%`;

  const total = num(m.totalRateInCents);
  if (total != null) return money(total);

  const maxUnits = num(m.maxUnitsPerCode);
  if (maxUnits != null) return `max ${maxUnits}`;

  return "";
};

/** A line is bundled when paying it absorbs other lines — a package or a case/per-diem rate. */
export const isBundled = (rec: IDraftRecommendation): boolean =>
  rec.componentType === "package_ruleset" ||
  rec.componentType === "case_rate_ruleset" ||
  rec.componentType === "exploding_fee_schedule";

/**
 * The line's name. Prefers whatever the construct calls itself, then the group name, then the service
 * line — falling back to the component type so a row is never blank and therefore never unreadable.
 */
export const describeRow = (rec: IDraftRecommendation): string => {
  const m = modeling(rec);
  return (
    str(m.displayName) ??
    str(m.name) ??
    str(m.groupName) ??
    str(m.baseRateFileDisplayName) ??
    serviceLineOf(rec) ??
    rec.componentType.replace(/_/g, " ")
  );
};

export const toGridRow = (rec: IDraftRecommendation): IRateGridRow => {
  const { codes, kind } = codesOf(rec);
  return {
    id: rec.id,
    description: describeRow(rec),
    codes,
    codeKind: kind,
    methodology: methodologyOf(rec),
    rate: rateOf(rec),
    bundled: isBundled(rec),
    carveOut: rec.componentType === "carveout",
    serviceLine: serviceLineOf(rec) ?? UNGROUPED,
    componentType: rec.componentType,
    confidence: rec.confidence ?? null,
    status: rec.status,
    needsInput: rec.status === "needs_input",
  };
};

export interface IRateGridGroup {
  serviceLine: string;
  rows: IRateGridRow[];
}

/**
 * Group rows by service line, the way v0's outpatient step reads.
 *
 * Named groups sort alphabetically and `Unassigned` is pinned last: it is the catch-all, and letting it
 * sort into the middle on the letter "U" would bury it among real clinical lines.
 */
export const toGridGroups = (
  recs: IDraftRecommendation[]
): IRateGridGroup[] => {
  const byLine = new Map<string, IRateGridRow[]>();
  recs.forEach((rec) => {
    const row = toGridRow(rec);
    const existing = byLine.get(row.serviceLine);
    if (existing) existing.push(row);
    else byLine.set(row.serviceLine, [row]);
  });

  return Array.from(byLine.entries())
    .map(([serviceLine, rows]) => ({ serviceLine, rows }))
    .sort((a, b) => {
      if (a.serviceLine === UNGROUPED) return 1;
      if (b.serviceLine === UNGROUPED) return -1;
      return a.serviceLine.localeCompare(b.serviceLine);
    });
};

/** Header counts — v0 shows "16 rate lines · 2 carve-outs · 6 bundled" above the grid. */
export const gridSummary = (
  groups: IRateGridGroup[]
): { lines: number; carveOuts: number; bundled: number } => {
  const rows = groups.flatMap((g) => g.rows);
  return {
    lines: rows.length,
    carveOuts: rows.filter((r) => r.carveOut).length,
    bundled: rows.filter((r) => r.bundled).length,
  };
};

/** Inpatient vs outpatient, for the two steps that render a grid. */
export const isInpatientRow = (rec: IDraftRecommendation): boolean =>
  rec.componentType === "inpatient_ruleset" ||
  careSettingOf(rec) === "inpatient";
